'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "children", "flush", "singleBreadcrumbIsBackLink", "truncateToLevels", "wrapLevels", "defaultOpen"];
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Children, cloneElement, isValidElement, Fragment } from 'react';
import UIIcon from '../icon/UIIcon';
import UIDropdown from '../dropdown/UIDropdown';
import UIList from '../list/UIList';
import { hidden } from '../utils/propTypes/decorators';
import { warnIfFragment } from '../utils/devWarnings';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const renderBreadcrumb = (children, hasArrow, isBackLink) => {
  return /*#__PURE__*/_jsxs("span", {
    children: [hasArrow && /*#__PURE__*/_jsx(UIIcon, {
      className: 'private-breadcrumbs__arrow' + (isBackLink ? " private-breadcrumbs__arrow--back" : ""),
      name: isBackLink ? 'left' : 'right'
    }), children]
  });
};
const renderChild = (child, index, totalCrumbs, isOnlyChild = false, singleBreadcrumbIsBackLink = false, isSROnly = false) => {
  const ariaCurrent = !isOnlyChild && index === totalCrumbs - 1 ? 'location' : null;
  const isBackLink = isOnlyChild && singleBreadcrumbIsBackLink;
  const hasArrow = isBackLink || index !== 0;
  const addedClassNames = 'private-breadcrumbs__item' + (isSROnly ? " sr-only sr-only-focusable" : "");
  warnIfFragment(child, 'UIBreadcrumbs');
  if ( /*#__PURE__*/isValidElement(child)) {
    return /*#__PURE__*/cloneElement(child, {
      'aria-current': ariaCurrent,
      children: renderBreadcrumb(child.props.children, hasArrow, isBackLink),
      key: index,
      className: addedClassNames
    });
  }
  return /*#__PURE__*/_jsx("span", {
    "aria-current": ariaCurrent,
    className: addedClassNames,
    children: renderBreadcrumb(child, hasArrow, isBackLink)
  });
};
const renderTruncateDropdown = (children, defaultOpen) => {
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UIIcon, {
      className: "private-breadcrumbs__arrow",
      name: "right"
    }), /*#__PURE__*/_jsx(UIDropdown, {
      autoPlacement: "vert",
      tabIndex: -1,
      buttonUse: "transparent",
      buttonText: /*#__PURE__*/_jsx("span", {
        className: "align-center",
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "ellipses",
          className: "m-right-0 m-left-1 m-top-1",
          size: 22
        })
      }),
      buttonClassName: "p-x-0",
      caretRenderer: () => null,
      defaultOpen: defaultOpen,
      menuWidth: "auto",
      placement: "bottom right",
      children: /*#__PURE__*/_jsx(UIList, {
        children: children
      })
    })]
  });
};
const renderTruncatedBreadcrumbs = (validChildren, isOnlyChild, singleBreadcrumbIsBackLink, levelsToShow, defaultOpen) => {
  const totalCrumbs = validChildren.length;
  const innerCrumbs = validChildren.slice(1, -levelsToShow);
  const innerCrumbLength = innerCrumbs.length;
  const updatedLevelsToShow = innerCrumbLength ? levelsToShow : totalCrumbs - 1;
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [renderChild(validChildren[0], 0, totalCrumbs, isOnlyChild, singleBreadcrumbIsBackLink), innerCrumbs.map((child, index) => {
      return renderChild(child, index + 1, totalCrumbs, isOnlyChild, singleBreadcrumbIsBackLink, true);
    }), innerCrumbLength ? renderTruncateDropdown(innerCrumbs, defaultOpen) : null, validChildren.map((child, index) => {
      if (index > totalCrumbs - updatedLevelsToShow - 1) {
        return renderChild(child, index, totalCrumbs, isOnlyChild, singleBreadcrumbIsBackLink);
      }
      return null;
    })]
  });
};
const renderChildren = (children, singleBreadcrumbIsBackLink, truncateToLevels, defaultOpen) => {
  const isValidChild = child => {
    if (typeof child === 'string') {
      return child.trim().length > 0;
    }
    return child;
  };
  const childrenArray = Children.toArray(children);
  const validChildren = childrenArray.filter(isValidChild);
  const totalCrumbs = validChildren.length;
  const isOnlyChild = totalCrumbs === 1;
  let breadcrumbs;
  if (truncateToLevels !== null && !isOnlyChild) {
    breadcrumbs = renderTruncatedBreadcrumbs(validChildren, isOnlyChild, singleBreadcrumbIsBackLink, truncateToLevels, defaultOpen);
  } else {
    breadcrumbs = Children.map(validChildren, (child, index) => {
      return renderChild(child, index, totalCrumbs, isOnlyChild, singleBreadcrumbIsBackLink);
    });
  }
  if (isOnlyChild && !singleBreadcrumbIsBackLink) {
    breadcrumbs.push( /*#__PURE__*/_jsx("span", {
      className: "private-breadcrumbs__item",
      children: renderBreadcrumb(null, true, false)
    }, "x"));
  }
  return breadcrumbs;
};
export default function UIBreadcrumbs(props) {
  const {
      className,
      children,
      flush,
      singleBreadcrumbIsBackLink,
      truncateToLevels,
      wrapLevels,
      defaultOpen
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx("nav", Object.assign({}, rest, {
    className: classNames('private-breadcrumbs', className, flush && 'private-breadcrumbs--flush', !(wrapLevels || truncateToLevels !== null) && 'private-breadcrumbs--no-wrap'),
    children: renderChildren(children, singleBreadcrumbIsBackLink, truncateToLevels, defaultOpen)
  }));
}
UIBreadcrumbs.propTypes = {
  children: PropTypes.node,
  defaultOpen: hidden(PropTypes.bool.isRequired),
  flush: PropTypes.bool.isRequired,
  singleBreadcrumbIsBackLink: PropTypes.bool.isRequired,
  truncateToLevels: PropTypes.number,
  wrapLevels: PropTypes.bool.isRequired,
  role: PropTypes.string
};
UIBreadcrumbs.defaultProps = {
  defaultOpen: false,
  flush: false,
  role: 'navigation',
  singleBreadcrumbIsBackLink: true,
  truncateToLevels: null,
  wrapLevels: false
};
UIBreadcrumbs.displayName = 'UIBreadcrumbs';